// Hurtigvalg for bilagskonto bruker allerede bokfort historikk. Forslagene er
// bare redigeringshjelp: brukeren bestemmer om konto, beskrivelse eller begge
// skal erstattes, og den endelige bokforingen blir neste historiesignal.
(function initReceiptAccountQuickChoices(global) {
  const MAX_CHOICES = 5;

  const safeArray = (value) => Array.isArray(value) ? value : [];
  const cleanText = (value) => String(value || '').trim();
  const normalizeText = (value) => cleanText(value)
    .toLocaleLowerCase('nb-NO')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const normalizeOrg = (value) => cleanText(value).replace(/\D/g, '');
  const accountCode = (value) => cleanText(value).replace(/\D/g, '').slice(0, 4);
  const accountLabelForCode = ({ code, fallbackName = '', accounts, separator = ' ' } = {}) => {
    const normalizedCode = accountCode(code);
    const account = safeArray(accounts).find((candidate) =>
      accountCode(candidate?.code || candidate?.nr) === normalizedCode);
    const name = cleanText(account?.name || account?.navn || fallbackName);
    return [normalizedCode, name].filter(Boolean).join(separator);
  };
  const isActiveExpense = (expense) => !['annulled', 'reversed', 'deleted', 'draft']
    .includes(cleanText(expense?.status).toLowerCase()) && !expense?.annulledAt;

  const sameSupplier = (expense, supplierName, supplierOrganizationNumber) => {
    const wantedOrg = normalizeOrg(supplierOrganizationNumber);
    const expenseOrg = normalizeOrg(expense?.supplierOrganizationNumber);
    if (wantedOrg.length >= 8 && expenseOrg.length >= 8) return wantedOrg === expenseOrg;
    const wantedName = normalizeText(supplierName);
    return wantedName.length >= 3 && normalizeText(expense?.supplierName) === wantedName;
  };

  const choiceRowsForExpense = (expense) => {
    const lines = safeArray(expense?.purchaseLines).filter((line) =>
      accountCode(line?.costAccountCode || line?.accountCode));
    const source = lines.length ? lines : [expense];
    return source.map((line) => ({
      accountCode: accountCode(line?.costAccountCode || line?.accountCode || expense?.costAccountCode),
      accountName: cleanText(line?.costAccountName || expense?.costAccountName),
      // Den overordnede beskrivelsen er teksten brukeren godkjente i panelet.
      // Linjeteksten er reserve for eldre kjop og importerte bilag.
      description: cleanText(expense?.description || line?.description),
      lastUsedAt: cleanText(expense?.updatedAt || expense?.date || expense?.createdAt),
    })).filter((choice) => choice.accountCode);
  };

  const analysisChoices = (analysis = {}) => {
    const lines = safeArray(analysis.purchaseLines).map((line) => ({
      accountCode: accountCode(line?.costAccountCode || line?.suggestedCostAccountCode),
      accountName: cleanText(line?.costAccountName || line?.suggestedCostAccountName),
      description: cleanText(analysis.description || line?.description || analysis.supplierName),
    }));
    return [{
      accountCode: accountCode(analysis.suggestedCostAccountCode),
      accountName: cleanText(analysis.suggestedCostAccountName),
      description: cleanText(analysis.description || analysis.supplierName),
    }, ...lines].filter((choice) => choice.accountCode);
  };

  // Ett kontonummer er ett hurtigvalg. Flere historiske beskrivelser for samme
  // konto skal ikke gi flere identiske lilla knapper.
  const choiceKey = (choice) => choice.accountCode;

  const build = ({ state, analysis, supplierName, supplierOrganizationNumber, availableAccountCodes } = {}) => {
    const allowed = availableAccountCodes instanceof Set ? availableAccountCodes : null;
    const history = new Map();
    safeArray(state?.expenses)
      .filter(isActiveExpense)
      .filter((expense) => sameSupplier(expense, supplierName, supplierOrganizationNumber))
      .forEach((expense) => choiceRowsForExpense(expense).forEach((choice) => {
        if (allowed?.size && !allowed.has(choice.accountCode)) return;
        const key = choiceKey(choice);
        const current = history.get(key) || { ...choice, count: 0, origin: 'history' };
        current.count += 1;
        if (choice.lastUsedAt > current.lastUsedAt) Object.assign(current, {
          accountName: choice.accountName || current.accountName,
          description: choice.description || current.description,
          lastUsedAt: choice.lastUsedAt,
        });
        history.set(key, current);
      }));

    const ranked = [...history.values()].sort((left, right) =>
      right.count - left.count || String(right.lastUsedAt).localeCompare(String(left.lastUsedAt)));
    const seen = new Set(ranked.map(choiceKey));
    analysisChoices(analysis).forEach((choice) => {
      if ((allowed?.size && !allowed.has(choice.accountCode)) || seen.has(choiceKey(choice))) return;
      ranked.push({ ...choice, count: 0, origin: 'analysis', lastUsedAt: '' });
      seen.add(choiceKey(choice));
    });
    return ranked.slice(0, MAX_CHOICES);
  };

  const apply = ({ draft, choice, mode, accountLabel } = {}) => {
    const next = { ...(draft || {}) };
    if (!choice || !['account', 'description', 'both'].includes(mode)) return next;
    if (mode === 'account' || mode === 'both') {
      next.account = cleanText(accountLabel) || [choice.accountCode, choice.accountName].filter(Boolean).join(' ');
    }
    if ((mode === 'description' || mode === 'both') && cleanText(choice.description)) {
      next.description = cleanText(choice.description);
    }
    return next;
  };

  global.PlummeReceiptAccountQuickChoices = { MAX_CHOICES, accountLabelForCode, build, apply };
})(typeof window !== 'undefined' ? window : globalThis);
